/* Custom announcement bar
   Cada breakpoint elige su modo: inline / slider / marquee (data-desktop-mode
   y data-mobile-mode). Soporta slide/fade, autoplay, loop, swipe, flechas,
   dots y marquee infinito con duración calculada según el ancho real. */

if (typeof CustomAnnouncementBar !== 'function') {
  class CustomAnnouncementBar extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.track = this.querySelector('[data-cab-track]');
      this.marqueeStage = this.querySelector('[data-cab-marquee]');
      this.marqueeInner = this.querySelector('[data-cab-marquee-inner]');
      this.marqueeGroup = this.querySelector('[data-cab-marquee-group]');
      this.marqueeSpeed = parseFloat(this.dataset.marqueeSpeed) || 50;
      this.items = Array.from(this.querySelectorAll('[data-cab-item]'));
      this.dots = Array.from(this.querySelectorAll('[data-cab-dot]'));
      this.prevButton = this.querySelector('[data-cab-prev]');
      this.nextButton = this.querySelector('[data-cab-next]');

      if (!this.track && !this.marqueeStage) return;

      this.autoplay = this.dataset.autoplay === 'true';
      this.autoplaySpeed = parseInt(this.dataset.autoplaySpeed, 10) || 5000;
      this.loop = this.dataset.loop === 'true';
      this.pauseOnHover = this.dataset.pauseOnHover === 'true';
      this.index = 0;
      this.timer = null;
      this.paused = false;

      this.mq = window.matchMedia('(min-width: 768px)');
      this.onBreakpointChange = () => this.refresh();
      if (this.mq.addEventListener) {
        this.mq.addEventListener('change', this.onBreakpointChange);
      } else {
        this.mq.addListener(this.onBreakpointChange);
      }

      if (this.prevButton) {
        this.prevButton.addEventListener('click', () => this.step(-1, true));
      }
      if (this.nextButton) {
        this.nextButton.addEventListener('click', () => this.step(1, true));
      }
      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          const position = this.visibleDots().indexOf(dot);
          if (position > -1) this.goTo(position, true);
        });
      });

      if (this.pauseOnHover) {
        this.addEventListener('mouseenter', () => {
          this.paused = true;
        });
        this.addEventListener('mouseleave', () => {
          this.paused = false;
        });
      }

      this.addEventListener('focusin', () => {
        this.paused = true;
      });
      this.addEventListener('focusout', () => {
        this.paused = false;
      });

      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          this.stopAutoplay();
        } else {
          this.startAutoplay();
        }
      });

      this.bindSwipe();

      if (window.Shopify && window.Shopify.designMode) {
        document.addEventListener('shopify:block:select', (event) => {
          if (!this.contains(event.target)) return;
          const visible = this.visibleItems();
          const position = visible.indexOf(event.target);
          if (position > -1) this.goTo(position, true);
        });
      }

      window.addEventListener('resize', () => {
        clearTimeout(this.resizeTimer);
        this.resizeTimer = setTimeout(() => this.refresh(), 150);
      });

      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => this.refresh());
      }

      this.refresh();
    }

    /* ------------------------------------------------------------ helpers */

    get sliderActive() {
      if (!this.track) return false;
      return this.mq.matches
        ? this.dataset.desktopMode === 'slider'
        : this.dataset.mobileMode === 'slider';
    }

    visibleItems() {
      return this.items.filter((item) => window.getComputedStyle(item).display !== 'none');
    }

    visibleDots() {
      return this.dots.filter((dot) => window.getComputedStyle(dot).display !== 'none');
    }

    /* ------------------------------------------------------------ control */

    refresh() {
      this.refreshMarquee();

      if (!this.track) return;

      const visible = this.visibleItems();

      if (!this.sliderActive || visible.length < 2) {
        this.stopAutoplay();
        this.track.style.transform = '';
        this.items.forEach((item) => {
          item.classList.add('is-active');
          item.removeAttribute('aria-hidden');
        });
        if (!this.sliderActive) return;
      }

      if (this.index > visible.length - 1) this.index = 0;
      this.goTo(this.index, false);
      this.startAutoplay();
    }

    goTo(index, userAction) {
      const visible = this.visibleItems();
      if (visible.length === 0) return;

      if (index < 0) index = this.loop ? visible.length - 1 : 0;
      if (index > visible.length - 1) index = this.loop ? 0 : visible.length - 1;
      this.index = index;

      if (this.sliderActive && visible.length > 1) {
        const target = visible[index];
        const offset = target.offsetLeft - visible[0].offsetLeft;
        this.track.style.transform = `translateX(${-offset}px)`;

        visible.forEach((item, position) => {
          item.classList.toggle('is-active', position === index);
          if (position === index) {
            item.removeAttribute('aria-hidden');
          } else {
            item.setAttribute('aria-hidden', 'true');
          }
        });
      }

      this.visibleDots().forEach((dot, position) => {
        dot.classList.toggle('is-active', position === index);
        dot.setAttribute('aria-current', position === index ? 'true' : 'false');
      });

      if (!this.loop) {
        if (this.prevButton) this.prevButton.disabled = index === 0;
        if (this.nextButton) this.nextButton.disabled = index === visible.length - 1;
      }

      if (userAction) this.startAutoplay();
    }

    step(direction, userAction) {
      this.goTo(this.index + direction, userAction);
    }

    /* ----------------------------------------------------------- autoplay */

    startAutoplay() {
      this.stopAutoplay();
      if (!this.autoplay || !this.sliderActive) return;
      if (this.visibleItems().length < 2) return;

      this.timer = setInterval(() => {
        if (this.paused || document.hidden) return;
        const visible = this.visibleItems();
        const next = this.index + 1 > visible.length - 1 ? 0 : this.index + 1;
        this.goTo(next, false);
      }, this.autoplaySpeed);
    }

    stopAutoplay() {
      if (this.timer) clearInterval(this.timer);
      this.timer = null;
    }

    /* -------------------------------------------------------------- swipe */

    /* ------------------------------------------------------------ marquee */

    refreshMarquee() {
      if (!this.marqueeInner || !this.marqueeGroup) return;
      if (window.getComputedStyle(this.marqueeStage).display === 'none') return;

      const groupWidth = this.marqueeGroup.getBoundingClientRect().width;
      if (!groupWidth) return;

      // Suficientes copias para cubrir el ancho visible sin dejar huecos.
      const stageWidth = this.marqueeStage.getBoundingClientRect().width;
      const needed = Math.max(2, Math.ceil(stageWidth / groupWidth) + 1);

      for (let i = this.marqueeInner.children.length; i < needed; i++) {
        const clone = this.marqueeGroup.cloneNode(true);
        clone.removeAttribute('data-cab-marquee-group');
        clone.setAttribute('aria-hidden', 'true');
        clone.querySelectorAll('[data-shopify-editor-block]').forEach((node) => {
          node.removeAttribute('data-shopify-editor-block');
        });
        this.marqueeInner.appendChild(clone);
      }

      this.style.setProperty('--cab-group-w', `${groupWidth}px`);
      this.style.setProperty('--cab-marquee-duration', `${groupWidth / this.marqueeSpeed}s`);
    }

    /* -------------------------------------------------------------- swipe */

    bindSwipe() {
      if (!this.track) return;

      let startX = 0;
      let startY = 0;
      let tracking = false;

      this.track.addEventListener(
        'touchstart',
        (event) => {
          if (!this.sliderActive) return;
          tracking = true;
          startX = event.touches[0].clientX;
          startY = event.touches[0].clientY;
        },
        { passive: true }
      );

      this.track.addEventListener(
        'touchend',
        (event) => {
          if (!tracking) return;
          tracking = false;
          const deltaX = event.changedTouches[0].clientX - startX;
          const deltaY = event.changedTouches[0].clientY - startY;
          if (Math.abs(deltaX) < 40 || Math.abs(deltaX) < Math.abs(deltaY)) return;
          const rtl = document.documentElement.getAttribute('dir') === 'rtl';
          const forward = rtl ? deltaX > 0 : deltaX < 0;
          this.step(forward ? 1 : -1, true);
        },
        { passive: true }
      );
    }

    disconnectedCallback() {
      this.stopAutoplay();
    }
  }

  if (typeof customElements.get('custom-announcement-bar') === 'undefined') {
    customElements.define('custom-announcement-bar', CustomAnnouncementBar);
  }
}
